import React, { useState } from "react";
import { useVaultStatus, useVaultSetup, useVaultUnlock } from "@/lib/queries";
import { Lock, ShieldCheck, KeyRound, ArrowRight } from "lucide-react";

export default function VaultGate({ children }) {
  const { data: status, isLoading } = useVaultStatus();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const setup = useVaultSetup();
  const unlock = useVaultUnlock();

  if (isLoading) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-[#050505] text-[#737373] text-xs uppercase tracking-[0.3em] font-mono">
        Loading vault…
      </div>
    );
  }

  // Cloud mode or already unlocked -> pass through
  if (!status?.local || !status?.locked) {
    return children;
  }

  const needSetup = !status.initialized;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Master password must be at least 8 characters");
      return;
    }
    if (needSetup && password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    try {
      if (needSetup) {
        await setup.mutateAsync(password);
      } else {
        await unlock.mutateAsync(password);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Incorrect master password");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] grain flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 border-2 border-[#EAB308] flex items-center justify-center bg-black mb-4">
            <Lock className="w-7 h-7 text-[#EAB308]" />
          </div>
          <h1 className="font-heading text-3xl font-black tracking-tighter uppercase text-center">
            {needSetup ? "Set master password" : "Unlock vault"}
          </h1>
          <p className="text-sm text-[#A3A3A3] mt-2 text-center max-w-xs">
            {needSetup
              ? "Choose a master password. It encrypts your store credentials, cards and proxies locally on this device. It cannot be recovered if forgotten."
              : "Enter your master password to decrypt store credentials, cards and proxies on this device."}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="vault-form">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1.5">
              Master password
            </div>
            <input
              data-testid="vault-password-input"
              type="password"
              autoFocus
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full bg-[#0d0d0d] border border-[#262626] text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308] placeholder:text-[#525252] font-mono"
            />
          </div>

          {needSetup && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1.5">
                Confirm password
              </div>
              <input
                data-testid="vault-confirm-input"
                type="password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-[#0d0d0d] border border-[#262626] text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308] placeholder:text-[#525252] font-mono"
              />
            </div>
          )}

          {error && (
            <div data-testid="vault-error" className="text-xs text-[#EF4444] bg-[#EF4444]/5 border border-[#EF4444]/30 px-3 py-2 rounded-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            data-testid="vault-submit-btn"
            disabled={setup.isPending || unlock.isPending}
            className="w-full bg-[#EAB308] text-black font-bold uppercase tracking-wider text-xs py-3 rounded-sm hover:bg-[#FDE047] transition-all flex items-center justify-center gap-2 disabled:opacity-60 shadow-[0_0_18px_rgba(234,179,8,0.2)]"
          >
            {needSetup ? <ShieldCheck className="w-4 h-4" /> : <KeyRound className="w-4 h-4" />}
            {needSetup ? "Set password & unlock" : "Unlock"}
            <ArrowRight className="w-4 h-4" />
          </button>
        </form>

        <div className="text-center text-[10px] font-mono text-[#737373] mt-8">
          AES-128 (Fernet) · PBKDF2-HMAC-SHA256 200k iterations
        </div>
      </div>
    </div>
  );
}
